import { Plus } from "lucide-react";
import { useState } from "react";

import type {
  AgendaItemSignalState,
  AgendaItemStatus,
} from "@elation/api-client/src/stitch-web";
import { useListAgendaItems } from "@elation/api-client/src/stitch-web";

import { DelayedRender } from "@src/shared/components/general/DelayedRender";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Input } from "@src/shared/components/ui/input";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import { Spinner } from "@src/shared/components/ui/spinner";

import { AgendaItem } from "./AgendaItem";
import {
  type AgendaItemMutationError,
  isPendingItem,
  useAgendaItemMutations,
} from "./useAgendaItemMutations";

const mutationErrorMessage = (error: AgendaItemMutationError): string => {
  if (error.status === 401) return "Your session expired. Please reload.";
  if (error.status === 403) return "You don't have permission to do that.";
  if (error.status === 404) return "That item no longer exists.";
  if (error.status !== null && error.status >= 400 && error.status < 500) {
    const verbMap = { create: "add", update: "update", delete: "remove" };
    return `Couldn't ${verbMap[error.verb]} that item.`;
  }
  return "Something went wrong. Please try again.";
};

export interface AgendaProps {
  encounterId?: number;
  patientId?: number;
  /**
   * Set by the parent when `useAppSSEEvents` has fallen back to polling
   * (SSE connection exhausted its retries). Enables a `refetchInterval` so the
   * list stays fresh without the event stream.
   */
  isFallbackPolling?: boolean;
}

export const Agenda = ({
  encounterId: encounterIdProp,
  patientId,
  isFallbackPolling = false,
}: AgendaProps) => {
  const encounterId =
    encounterIdProp !== undefined &&
    encounterIdProp > 0 &&
    !Number.isNaN(encounterIdProp)
      ? encounterIdProp
      : undefined;

  const [newItemDescription, setNewItemDescription] = useState("");

  const { data: agendaItems, isFetching: isFetchingAgendaItems } =
    useListAgendaItems(encounterId ?? -1, {
      query: {
        enabled: !!encounterId,
        refetchInterval: isFallbackPolling ? 5000 : undefined,
      },
    });

  const { createAgendaItem, toggleAgendaItem, deleteAgendaItem, error } =
    useAgendaItemMutations({
      encounterId,
      patientId,
    });

  const handleCreateAgendaItem = async (description: string) => {
    await createAgendaItem(description);
  };

  const handleToggleComplete = async (
    itemId: number,
    currentStatus: AgendaItemStatus,
    currentSignalState: AgendaItemSignalState,
  ) => {
    try {
      await toggleAgendaItem(itemId, currentStatus, currentSignalState);
    } catch {
      return;
    }
  };

  const handleDeleteAgendaItem = async (itemId: number) => {
    try {
      await deleteAgendaItem(itemId);
    } catch {
      return;
    }
  };

  const loadingText = "Loading...";

  const isPreparingEncounter = encounterId == null;
  const items = agendaItems?.data.items ?? [];
  const hasItems = items.length > 0;

  return (
    <VerticalFlexbox gap="sm">
      <VerticalFlexbox
        as="ul"
        gap="xs"
        data-testid="agenda-list"
        aria-label="Agenda"
        aria-busy={isPreparingEncounter || isFetchingAgendaItems}
      >
        {isPreparingEncounter
          ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)
          : items.map((item) => (
              <AgendaItem
                key={item.id}
                description={item.description}
                type={item.type}
                status={item.status}
                signalState={item.signalState}
                isPending={isPendingItem(item.id)}
                onToggleComplete={() =>
                  handleToggleComplete(item.id, item.status, item.signalState)
                }
                onRemove={() => handleDeleteAgendaItem(item.id)}
              />
            ))}
      </VerticalFlexbox>
      {error ? (
        <HorizontalFlexbox alignItems="center" gap="xs" className="px-1">
          <span className="text-sm text-destructive">
            {mutationErrorMessage(error)}
          </span>
        </HorizontalFlexbox>
      ) : null}
      {isFetchingAgendaItems ? (
        <DelayedRender delay={300}>
          <HorizontalFlexbox alignItems="center" gap="xs" className="px-1">
            <Spinner size="sm" label={loadingText} />
          </HorizontalFlexbox>
        </DelayedRender>
      ) : null}
      {hasItems ? (
        <Text sizing="xs" appearance="subdued" className="px-1">
          Items check off automatically as you discuss them while scribing.
        </Text>
      ) : !isPreparingEncounter && !isFetchingAgendaItems ? (
        <Text sizing="xs" appearance="subdued" className="px-1">
          Add items from the briefing, or type your own. They check off as you
          cover them.
        </Text>
      ) : null}
      <div className="relative">
        <Plus className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={
            isPreparingEncounter ? "Preparing session..." : "Add item..."
          }
          value={newItemDescription}
          onChange={(e) => setNewItemDescription(e.target.value)}
          onKeyDown={async (e) => {
            if (e.key !== "Enter") return;
            const description = newItemDescription.trim();
            if (description === "") return;
            setNewItemDescription("");
            try {
              await handleCreateAgendaItem(description);
            } catch {
              setNewItemDescription((current) =>
                current === "" ? description : current,
              );
            }
          }}
          aria-label="Add item"
          className="pl-9"
          disabled={isPreparingEncounter}
        />
      </div>
    </VerticalFlexbox>
  );
};
